import { useState } from 'react';

import { MAX_TURN_POINTS } from '@/core/engine/x01/turn';
import { cn } from '@/shared/utils/cn';

interface SumKeypadProps {
  /** Remaining score of the throwing side, for a plausibility hint. */
  remaining: number;
  disabled: boolean;
  onSubmit: (points: number) => void;
}

const QUICK = [26, 41, 45, 60, 81, 85, 100, 140, 180];

/**
 * Turn-total entry for sum mode: type the Aufnahme (0–180) or tap a common
 * value. Large targets for a phone held at the board.
 */
export function SumKeypad({ remaining, disabled, onSubmit }: SumKeypadProps) {
  const [value, setValue] = useState('');

  const points = value === '' ? 0 : Number(value);
  const tooHigh = points > MAX_TURN_POINTS;

  const submit = (n: number) => {
    if (disabled || n > MAX_TURN_POINTS) {
      return;
    }
    onSubmit(n);
    setValue('');
  };

  const digit = (d: string) => {
    const next = (value + d).replace(/^0+(?=\d)/, '');
    if (Number(next) <= MAX_TURN_POINTS) {
      setValue(next);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-fg-subtle text-sm">Aufnahme</span>
        <span
          className={cn(
            'font-mono text-3xl font-bold tabular-nums',
            tooHigh ? 'text-danger' : 'text-fg',
          )}
        >
          {value === '' ? '0' : value}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {QUICK.map((q) => (
          <button
            key={q}
            type="button"
            disabled={disabled}
            onClick={() => submit(q)}
            className="bg-surface-raised border-line hover:bg-surface-overlay touch-target rounded-lg border py-3 text-lg font-semibold disabled:opacity-40"
          >
            {q}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button
            key={d}
            type="button"
            disabled={disabled}
            onClick={() => digit(d)}
            className="bg-surface border-line hover:bg-surface-raised touch-target rounded-lg border py-3 text-xl disabled:opacity-40"
          >
            {d}
          </button>
        ))}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setValue('')}
          className="bg-surface border-line hover:bg-surface-raised touch-target rounded-lg border py-3 text-sm disabled:opacity-40"
        >
          C
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => digit('0')}
          className="bg-surface border-line hover:bg-surface-raised touch-target rounded-lg border py-3 text-xl disabled:opacity-40"
        >
          0
        </button>
        <button
          type="button"
          disabled={disabled || tooHigh}
          onClick={() => submit(points)}
          className="bg-brand text-brand-fg hover:bg-brand-hover touch-target rounded-lg py-3 text-lg font-semibold disabled:opacity-40"
        >
          OK
        </button>
      </div>

      <p className="text-fg-subtle text-center text-xs">Rest {remaining}</p>
    </div>
  );
}
